import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";

type Phase = "input" | "typing" | "feed";

const PHONE_HEIGHT = 480;

const TypewriterPreview: React.FC = () => {
  const { t } = useTranslation("home");
  const [phase, setPhase] = useState<Phase>("input");
  const [typedText, setTypedText] = useState("");

  const inputText = t("typewriter_input");
  const fullOutput = t("typewriter_output");
  const brandName = t("typewriter_brand");

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    let interval: ReturnType<typeof setInterval>;

    if (phase === "input") {
      timeout = setTimeout(() => setPhase("typing"), 1500);
    } else if (phase === "typing") {
      let i = 0;
      interval = setInterval(() => {
        i++;
        setTypedText(fullOutput.slice(0, i));
        if (i >= fullOutput.length) {
          clearInterval(interval);
          timeout = setTimeout(() => setPhase("feed"), 800);
        }
      }, 35);
    } else if (phase === "feed") {
      timeout = setTimeout(() => {
        setTypedText("");
        setPhase("input");
      }, 3500);
    }

    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
    };
  }, [phase, fullOutput]);

  return (
    <div
      className="relative mx-auto w-full max-w-[280px] md:max-w-[320px]"
      style={{ height: PHONE_HEIGHT + 24 }}
    >
      <div className="relative rounded-[2.5rem] border-2 border-gray-700 bg-gray-950 p-3 shadow-neon-blue glow-blue h-full">
        <div className="absolute top-4 left-1/2 -translate-x-1/2 w-20 h-5 bg-gray-900 rounded-full z-10" />
        <div
          className="rounded-[2rem] overflow-hidden bg-space-light flex flex-col"
          style={{ height: PHONE_HEIGHT }}
        >
          <div className="px-4 pt-10 pb-3 border-b border-white/5 shrink-0">
            <p className="text-caption text-neon-blue mb-1">港微創 AI</p>
            <div className="bg-space rounded-lg px-3 py-2 text-body-sm text-gray-300 border border-white/10">
              {inputText}
              <span className="inline-block w-0.5 h-4 bg-neon-green ml-0.5 animate-pulse" />
            </div>
          </div>

          <div className="flex-1 p-4 overflow-y-auto overflow-x-hidden">
            <div className="flex flex-col gap-2 min-h-0">
              <AnimatePresence mode="wait">
                {(phase === "typing" || phase === "feed") && (
                  <motion.div
                    key="output"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="bg-space/80 rounded-lg p-3 border border-neon-green/20 shrink-0"
                  >
                    <p className="text-body-sm text-gray-200 leading-relaxed line-clamp-3">
                      {typedText}
                      {phase === "typing" && (
                        <span className="inline-block w-0.5 h-3 bg-neon-green ml-0.5 animate-pulse" />
                      )}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>

              <AnimatePresence>
                {phase === "feed" && (
                  <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 6 }}
                    className="rounded-xl overflow-hidden border border-white/10 shrink-0"
                  >
                    <div className="h-[72px] bg-gradient-to-br from-neon-blue/30 via-purple-900/40 to-neon-green/30 flex items-center justify-center">
                      <span className="text-3xl">🍪</span>
                    </div>
                    <div className="p-2.5 bg-space">
                      <div className="flex gap-2 mb-1">
                        <div className="w-5 h-5 rounded-full bg-gradient-brand shrink-0" />
                        <span className="text-[10px] text-gray-400 truncate">
                          {brandName}
                        </span>
                      </div>
                      <p className="text-[10px] text-gray-300 line-clamp-2">
                        {fullOutput.slice(0, 50)}...
                      </p>
                      <div className="flex gap-2 mt-1 text-gray-500 text-[10px]">
                        <span>❤️ 128</span>
                        <span>💬 24</span>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TypewriterPreview;
